/**
 * reloadSettings — pure, import-free helpers for the SMS auto-reload settings
 * card (My Account → SMS Credits → SmsCreditsTab.vue).
 *
 * Kept side-effect-free so they unit-test directly (mirrored in
 * cypress/node-tests/wallet-reload-settings.test.js — KEEP IN SYNC) and so the
 * component's client-side validation matches the SAME bounds the backend
 * (save-reload-settings) enforces instead of drifting.
 *
 * Backend contract (save-reload-settings validates, 400 on bad input):
 *   trigger 1–500 · amount 5–1000 · cap (when set) ≥ amount & ≤ 10000.
 *   A blank/null cap means "no monthly limit".
 */

export const RELOAD_TRIGGER_MIN = 1;
export const RELOAD_TRIGGER_MAX = 500;
export const RELOAD_AMOUNT_MIN = 5;
export const RELOAD_AMOUNT_MAX = 1000;
export const RELOAD_CAP_MAX = 10000;

// Is a cap value actually provided (vs blank = "no limit")?
export function capProvided(cap) {
  return cap !== null && cap !== undefined && String(cap).trim() !== '';
}

/**
 * Validate the three auto-reload inputs against the backend bounds.
 * Returns { valid, errors: { trigger, amount, cap } } — each error is a
 * human-readable message or null. `cap` is optional (blank = no limit) and is
 * only checked when provided; when set it must be ≥ the top-up amount and
 * ≤ RELOAD_CAP_MAX.
 */
export function validateReloadSettings({ trigger, amount, cap } = {}) {
  const errors = { trigger: null, amount: null, cap: null };

  const t = Number(trigger);
  if (!Number.isFinite(t) || t < RELOAD_TRIGGER_MIN || t > RELOAD_TRIGGER_MAX) {
    errors.trigger = `Enter a value between $${RELOAD_TRIGGER_MIN} and $${RELOAD_TRIGGER_MAX}.`;
  }

  const a = Number(amount);
  if (!Number.isFinite(a) || a < RELOAD_AMOUNT_MIN || a > RELOAD_AMOUNT_MAX) {
    errors.amount = `Enter a value between $${RELOAD_AMOUNT_MIN} and $${RELOAD_AMOUNT_MAX.toLocaleString()}.`;
  }

  if (capProvided(cap)) {
    const c = Number(cap);
    if (!Number.isFinite(c) || c > RELOAD_CAP_MAX || (Number.isFinite(a) && c < a)) {
      errors.cap = `Enter a limit at least your top-up amount and no more than $${RELOAD_CAP_MAX.toLocaleString()}.`;
    }
  }

  const valid = !errors.trigger && !errors.amount && !errors.cap;
  return { valid, errors };
}

/**
 * Credits at/below which a site with auto-reload OFF is "low" — the trigger
 * dollar amount converted to credits at the current per-credit price.
 * Mirrors the SmsCreditsTab expression exactly:
 *   Math.round((triggerUsd || 5) / (creditPriceUsd || 0.025))
 */
export function lowThresholdCredits(triggerUsd, creditPriceUsd) {
  return Math.round((triggerUsd || 5) / (creditPriceUsd || 0.025));
}
